import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  ScrollView,
  TouchableOpacity,
  Switch,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { Ionicons } from '@expo/vector-icons';
import { RootStackParamList } from '../types/navigation';
import { colors } from '../utils/colors';
import { typography } from '../utils/typography';
import { spacing } from '../utils/theme';

type NavigationProp = NativeStackNavigationProp<RootStackParamList>;

export const PrivacySettingsScreen: React.FC = () => {
  const navigation = useNavigation<NavigationProp>();
  const [privateAccount, setPrivateAccount] = useState(false);
  const [hideRepliesFromStrangers, setHideRepliesFromStrangers] = useState(false);
  const [allowTagging, setAllowTagging] = useState(true);

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity 
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Ionicons name="arrow-back" size={24} color={colors.text} />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>プライバシー設定</Text>
        <View style={styles.headerSpacer} />
      </View>

      <ScrollView>
        {/* アカウントプライバシーセクション */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>アカウント</Text>

          <View style={styles.settingItem}>
            <View style={styles.settingInfo}>
              <Text style={styles.settingLabel}>非公開アカウント</Text>
              <Text style={styles.settingDescription}>
                フォロワーのみが投稿を閲覧できます（今後対応予定）
              </Text>
            </View>
            <Switch
              value={privateAccount}
              onValueChange={setPrivateAccount}
              trackColor={{ false: colors.border, true: colors.primaryLight }}
              thumbColor={privateAccount ? colors.primary : colors.textSecondary}
              disabled={true}
            />
          </View>
        </View>

        {/* コンテンツ管理セクション */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>コンテンツ管理</Text>

          <TouchableOpacity
            style={styles.option}
            onPress={() => navigation.navigate('MutedUsers' as any)}
          >
            <View style={styles.optionLeft}>
              <Ionicons
                name="volume-mute-outline"
                size={22}
                color={colors.textSecondary}
              />
              <Text style={styles.optionLabel}>ミュートしたアカウント</Text>
            </View>
            <Ionicons
              name="chevron-forward"
              size={22}
              color={colors.textSecondary}
            />
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.option}
            onPress={() => navigation.navigate('MutedWords' as any)}
          >
            <View style={styles.optionLeft}>
              <Ionicons
                name="text-outline"
                size={22}
                color={colors.textSecondary}
              />
              <Text style={styles.optionLabel}>ミュートしたワード</Text>
            </View>
            <Ionicons
              name="chevron-forward"
              size={22}
              color={colors.textSecondary}
            />
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.option}
            onPress={() => navigation.navigate('BlockedUsers' as any)}
          >
            <View style={styles.optionLeft}>
              <Ionicons
                name="remove-circle-outline"
                size={22}
                color={colors.textSecondary}
              />
              <Text style={styles.optionLabel}>ブロックしたアカウント</Text>
            </View>
            <Ionicons
              name="chevron-forward"
              size={22}
              color={colors.textSecondary}
            />
          </TouchableOpacity>
        </View>

        {/* インタラクション設定セクション */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>インタラクション</Text>

          <View style={styles.settingItem}>
            <View style={styles.settingInfo}>
              <Text style={styles.settingLabel}>フォロー外からの返信を非表示</Text>
              <Text style={styles.settingDescription}>
                フォローしていないユーザーからの返信を表示しません
              </Text>
            </View>
            <Switch
              value={hideRepliesFromStrangers}
              onValueChange={setHideRepliesFromStrangers}
              trackColor={{ false: colors.border, true: colors.primaryLight }}
              thumbColor={hideRepliesFromStrangers ? colors.primary : colors.textSecondary}
            />
          </View>

          <View style={styles.settingItem}>
            <View style={styles.settingInfo}>
              <Text style={styles.settingLabel}>タグ付けを許可</Text>
              <Text style={styles.settingDescription}>
                他のユーザーがあなたをタグ付けできます
              </Text>
            </View>
            <Switch
              value={allowTagging}
              onValueChange={setAllowTagging}
              trackColor={{ false: colors.border, true: colors.primaryLight }}
              thumbColor={allowTagging ? colors.primary : colors.textSecondary}
            />
          </View>
        </View>

        {/* データとセキュリティセクション */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>データとセキュリティ</Text>
          <Text style={styles.sectionDescription}>
            Blueskyはオープンプロトコルを使用しているため、一部のデータは公開されます。
            詳細はBlueskyのプライバシーポリシーをご確認ください。
          </Text>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    backgroundColor: colors.background,
  },
  backButton: {
    padding: 4,
    width: 32,
  },
  headerTitle: {
    ...typography.h2,
    color: colors.text,
    flex: 1,
    textAlign: 'center',
  },
  headerSpacer: {
    width: 32,
  },
  section: {
    marginTop: spacing.lg,
    paddingHorizontal: spacing.md,
  },
  sectionTitle: {
    ...typography.h4,
    color: colors.textSecondary,
    marginBottom: spacing.xs,
  },
  sectionDescription: {
    ...typography.caption,
    color: colors.textSecondary,
    marginBottom: spacing.sm,
  },
  option: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  optionLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  optionLabel: {
    ...typography.body,
    color: colors.text,
    marginLeft: spacing.md,
  },
  settingItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  settingInfo: {
    flex: 1,
    marginRight: spacing.md,
  },
  settingLabel: {
    ...typography.body,
    color: colors.text,
    marginBottom: 4,
  },
  settingDescription: {
    ...typography.caption,
    color: colors.textSecondary,
  },
});
